/**
 * What NxTable keeps in the user's settings (I-2510), read and written through
 * the kit's settings contract (`useNxSettings`):
 *
 * - The settings key `table`: the defaults every table starts from — zebra,
 *   grid lines, search row, scrollbars, row density, the header's fill and
 *   where cells sit in a taller row. One set for all tables; the gear's
 *   „Einstellungen" dialog of any table edits it.
 * - `table.tables.<settings key>`: one table's own settings over everybody's
 *   (I-2510) — the same fields, set from that table's gear with „Diese
 *   Tabelle"; „Überall übernehmen" removes them. A tenant's or company's lock
 *   of a field holds for them too (master drops and refuses an override of a
 *   locked field).
 * - A table's own key (the `settingsKey` prop, e.g. `erp.grid.quotes`): its
 *   named filter sets, `filter_sets` — a map by id, so saving or deleting one
 *   set is a merge patch that leaves the others alone.
 */
import type {
  FilterModifier,
  NxSavedTableFilter,
  NxTableColumnFilter,
  NxTableFilterCondition,
  NxTableFilterGroup,
  NxTableFilterLeaf,
} from './filterModel';
import type { NxTableRowDensity } from './internal';

import type { NxRowOpenSettings } from './rowOpen';

/** The settings key of the table defaults. */
export const NX_TABLE_SETTINGS_KEY = 'table';

/**
 * The document under `table`. A field that is not set keeps the kit default;
 * without `row_density` every table keeps its own (`rowDensity` prop).
 */
export interface NxTableSettings {
  zebra?: 'on' | 'off';
  search_row?: boolean;
  always_show_scrollbars?: boolean;
  horizontal_lines?: boolean;
  vertical_lines?: boolean;
  row_density?: NxTableRowDensity;
  /** The header row: `subtle` (the neutral chrome, the default) or `brand` (filled with the brand colour). */
  header?: 'subtle' | 'brand';
  /** Where a cell's content sits in a row taller than one line: `middle` (the default) or `top`. */
  cell_align?: 'middle' | 'top';
  /** How a record list opens a record, per gesture (`recordList`, I-3641). */
  row_open?: NxRowOpenSettings;
  /** One table's own settings over these, by the table's `settingsKey` (I-2510). */
  tables?: Record<string, NxTableOwnSettings>;
}

/** One table's own settings: the fields of `table`, each over everybody's value. */
export type NxTableOwnSettings = Omit<NxTableSettings, 'tables'>;

/** The field of `table` that holds the tables' own settings. */
export const NX_TABLE_OWN_SETTINGS_FIELD = 'tables';

/** The fields of `table`, e.g. for a reset that removes them all. */
export const NX_TABLE_SETTINGS_FIELDS = [
  'zebra',
  'search_row',
  'always_show_scrollbars',
  'horizontal_lines',
  'vertical_lines',
  'row_density',
  'header',
  'cell_align',
  'row_open',
] as const satisfies readonly (keyof NxTableSettings)[];

/** The chrome a table draws with: the `table` defaults read with the kit's fallbacks. */
export interface NxTableChrome {
  zebraStripes: boolean;
  searchRowVisible: boolean;
  alwaysShowScrollbars: boolean;
  horizontalLines: boolean;
  verticalLines: boolean;
  /** `null`: the user set none — the table's own density applies. */
  rowDensity: NxTableRowDensity | null;
  /** The header row is filled with the brand colour (`header: brand`). */
  headerBrand: boolean;
  /** Cells sit at the top of a taller row (`cell_align: top`), not in its middle. */
  cellsTop: boolean;
}

const ROW_DENSITIES: readonly NxTableRowDensity[] = ['airy', 'normal', 'compact'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** One table's own settings in the `table` document (`tables.<key>`); `{}` without any. */
export function tableOwnSettingsOf(
  settings: unknown,
  key: string | null | undefined
): NxTableOwnSettings {
  if (!key || !isRecord(settings) || !isRecord(settings.tables)) return {};
  const own = settings.tables[key];
  return isRecord(own) ? (own as NxTableOwnSettings) : {};
}

/**
 * The settings one table draws with: everybody's (`table`) with the table's
 * own (`tables.<key>`) over them, field by field.
 */
export function tableSettingsFor(
  settings: unknown,
  key: string | null | undefined
): NxTableOwnSettings {
  const doc = isRecord(settings) ? settings : {};
  const shared = Object.fromEntries(
    Object.entries(doc).filter(([field]) => field !== NX_TABLE_OWN_SETTINGS_FIELD)
  );
  return { ...shared, ...tableOwnSettingsOf(doc, key) };
}

/** How many tables have settings of their own in the `table` document. */
export function tablesWithOwnSettings(settings: unknown): number {
  if (!isRecord(settings) || !isRecord(settings.tables)) return 0;
  return Object.values(settings.tables).filter(
    (own) => isRecord(own) && Object.keys(own).length > 0
  ).length;
}

/** Reads the `table` document leniently: anything missing or malformed keeps the kit default. */
export function tableChromeOf(settings: unknown): NxTableChrome {
  const doc = isRecord(settings) ? settings : {};
  return {
    zebraStripes: doc.zebra !== 'off',
    searchRowVisible: flag(doc.search_row, true),
    alwaysShowScrollbars: flag(doc.always_show_scrollbars, true),
    horizontalLines: flag(doc.horizontal_lines, true),
    verticalLines: flag(doc.vertical_lines, false),
    rowDensity: ROW_DENSITIES.includes(doc.row_density as NxTableRowDensity)
      ? (doc.row_density as NxTableRowDensity)
      : null,
    headerBrand: doc.header === 'brand',
    cellsTop: doc.cell_align === 'top',
  };
}

/* ── Named filter sets ─────────────────────────────────────────────────── */

/**
 * One named filter set as a table's settings keep it (`filter_sets.<id>`):
 * the three filter layers of the table (`filterModel.ts`) and how the columns
 * combine. Empty layers are left out — stored settings never hold `null`.
 */
export interface NxTableFilterSetDocument {
  name: string;
  /** Place among the sets (see `NxSavedTableFilter.order`); a set the user saves has none. */
  order?: number;
  filters: {
    /** How the per-column results combine (default `and`). */
    modifier?: FilterModifier;
    /** Search-row values per column. */
    quick?: Record<string, string[]>;
    /** Header-popover conditions per column. */
    columns?: Record<string, NxTableColumnFilter>;
    /** The wizard's condition tree. */
    advanced?: NxTableFilterGroup;
  };
}

const isModifier = (value: unknown): value is FilterModifier => value === 'and' || value === 'or';

function isCondition(value: unknown): value is NxTableFilterCondition {
  if (!isRecord(value) || typeof value.operator !== 'string') return false;
  if (value.value !== undefined && typeof value.value !== 'string') return false;
  if (value.valueTo !== undefined && typeof value.valueTo !== 'string') return false;
  return (
    value.values === undefined ||
    (Array.isArray(value.values) && value.values.every((item) => typeof item === 'string'))
  );
}

function isColumnFilter(value: unknown): value is NxTableColumnFilter {
  return (
    isRecord(value) &&
    isModifier(value.modifier) &&
    Array.isArray(value.conditions) &&
    value.conditions.every(isCondition)
  );
}

function isFilterNode(value: unknown): value is NxTableFilterGroup | NxTableFilterLeaf {
  if (!isRecord(value)) return false;
  if ('children' in value) return isFilterGroupDocument(value);
  return typeof value.columnId === 'string' && isCondition(value.condition);
}

function isFilterGroupDocument(value: unknown): value is NxTableFilterGroup {
  return (
    isRecord(value) &&
    isModifier(value.modifier) &&
    Array.isArray(value.children) &&
    value.children.every(isFilterNode)
  );
}

function quickFiltersOf(raw: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!isRecord(raw)) return out;
  for (const [columnId, values] of Object.entries(raw)) {
    if (Array.isArray(values) && values.every((item) => typeof item === 'string')) {
      out[columnId] = [...values];
    }
  }
  return out;
}

function columnFiltersOf(raw: unknown): Record<string, NxTableColumnFilter> {
  const out: Record<string, NxTableColumnFilter> = {};
  if (!isRecord(raw)) return out;
  for (const [columnId, filter] of Object.entries(raw)) {
    if (isColumnFilter(filter)) out[columnId] = filter;
  }
  return out;
}

const isOrder = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Sets with an order first (lowest first), then the others by name. */
function compareFilterSets(a: NxSavedTableFilter, b: NxSavedTableFilter): number {
  const orderA = a.order ?? Number.POSITIVE_INFINITY;
  const orderB = b.order ?? Number.POSITIVE_INFINITY;
  if (orderA !== orderB) return orderA < orderB ? -1 : 1;
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

/**
 * The named filter sets of a table's settings document: the ones with an
 * `order` first, then the others by name. A set that is not one (no name,
 * malformed filters) is left out; malformed columns inside an otherwise valid
 * set are dropped one by one.
 */
export function filterSetsFromSettings(doc: unknown): NxSavedTableFilter[] {
  const sets = isRecord(doc) && isRecord(doc.filter_sets) ? doc.filter_sets : {};
  const out: NxSavedTableFilter[] = [];
  for (const [id, raw] of Object.entries(sets)) {
    if (!isRecord(raw) || typeof raw.name !== 'string' || !raw.name.trim()) continue;
    if (!isRecord(raw.filters)) continue;
    const filters = raw.filters;
    out.push({
      id,
      name: raw.name,
      ...(isOrder(raw.order) ? { order: raw.order } : {}),
      filterModifier: isModifier(filters.modifier) ? filters.modifier : 'and',
      quickFilters: quickFiltersOf(filters.quick),
      columnFilters: columnFiltersOf(filters.columns),
      advancedFilter: isFilterGroupDocument(filters.advanced) ? filters.advanced : null,
    });
  }
  return out.sort(compareFilterSets);
}

/** A filter set as the settings keep it (see `NxTableFilterSetDocument`). */
export function filterSetToSettings(set: Omit<NxSavedTableFilter, 'id'>): NxTableFilterSetDocument {
  const quick = Object.fromEntries(
    Object.entries(set.quickFilters).filter(([, values]) => values.length > 0)
  );
  const filters: NxTableFilterSetDocument['filters'] = { modifier: set.filterModifier };
  if (Object.keys(quick).length) filters.quick = quick;
  if (Object.keys(set.columnFilters).length) filters.columns = set.columnFilters;
  if (set.advancedFilter) filters.advanced = set.advancedFilter;
  return { name: set.name, ...(isOrder(set.order) ? { order: set.order } : {}), filters };
}

/** A fresh id for a filter set: sortable by creation time, unique enough per table. */
export function newFilterSetId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
